import { z } from "zod";
import {
	isOrgScopedConfigurationTypesQuery,
	mergeConfigurationTypesById,
	ORG_SCOPED_CONFIGURATION_TYPES_FETCH_CAP,
	sliceConfigurationTypesPage,
	sortConfigurationTypes,
	stripEnabledParam,
} from "@/utils/configuration-type-list/configuration-type-org-scoped-list.utils";
import { isRequestAborted } from "@/utils/is-request-aborted";
import { shouldShowOrgAdminRecordTypesTableRow } from "@/utils/record-type-detail/record-type-detail.utils";
import { apiService, organizationIdHeader } from "../api.service";
import { type AuditLogDisplay, AuditLogDisplaySchema } from "../job/job.types";
import {
	appendAttachmentRequirementTagId,
	createTypeAttachmentRequirementTag,
	deleteTypeAttachmentRequirementTag,
	editTypeAttachmentRequirementTag,
	getTypeAttachmentRequirement,
	putTypeAttachmentRequirement,
} from "../shared/attachment-requirement.api";
import {
	type GeometrySourceDisplay,
	GeometrySourcesArraySchema,
	type GpsMethodDisplay,
	GpsMethodsArraySchema,
} from "../shared/geometry-catalog.types";
import type { CreateNoteInfo, NoteDisplay } from "../shared/note.types";
import { CreateNoteInfoSchema } from "../shared/note.types";
import {
	type CreateRecordInfo,
	CreateRecordInfoSchema,
	type CreateRecordTypeInfo,
	CreateRecordTypeInfoSchema,
	type EditRecordFileInfo,
	type EditRecordInfo,
	EditRecordInfoSchema,
	type EditRecordTypeInfo,
	EditRecordTypeInfoSchema,
	type RecordCountParams,
	type RecordDisplay,
	RecordDisplaySchema,
	type RecordDisplayWithUsers,
	RecordDisplayWithUsersSchema,
	type RecordFileDisplay,
	RecordFileDisplayArraySchema,
	type RecordFileParams,
	type RecordInstanceListParams,
	RecordNoteDisplayArraySchema,
	type RecordNoteParams,
	type RecordType,
	type RecordTypeDisplay,
	RecordTypeDisplaySchema,
	type RecordTypesAllParams,
	RecordTypesArraySchema,
	type RecordTypesListParams,
	type ServiceResult,
	ServiceResultSchema,
} from "./record.types";

const AuditLogArray = z.array(AuditLogDisplaySchema);
const RecordsArray = z.array(RecordDisplaySchema);

/**
 * Flatten nested `BoundingBox` to dotted query keys the API expects
 * (e.g. `BoundingBox.MinLatitude`).
 */
function toRecordQueryParams(
	params?: RecordCountParams | RecordInstanceListParams,
):
	| Record<string, string | number | boolean | string[] | undefined>
	| undefined {
	if (!params) return undefined;
	const flat: Record<string, string | number | boolean | string[] | undefined> =
		{};
	const { BoundingBox, ...rest } = params;
	if (BoundingBox) {
		if (BoundingBox.MinLatitude !== undefined)
			flat["BoundingBox.MinLatitude"] = BoundingBox.MinLatitude;
		if (BoundingBox.MaxLatitude !== undefined)
			flat["BoundingBox.MaxLatitude"] = BoundingBox.MaxLatitude;
		if (BoundingBox.MinLongitude !== undefined)
			flat["BoundingBox.MinLongitude"] = BoundingBox.MinLongitude;
		if (BoundingBox.MaxLongitude !== undefined)
			flat["BoundingBox.MaxLongitude"] = BoundingBox.MaxLongitude;
	}
	for (const [k, v] of Object.entries(rest)) {
		if (v !== undefined) flat[k] = v as string | number | boolean | string[];
	}
	return Object.keys(flat).length ? flat : undefined;
}

const RECORD_TYPE_ORDER_BY_ACCESSOR: Record<
	string,
	(row: RecordType) => string | number | null | undefined
> = {
	Name: (row) => row.name ?? "",
	Description: (row) => row.description ?? "",
	Id: (row) => row.id ?? "",
};

function filterOrgAdminRecordTypesTableRows(rows: RecordType[]): RecordType[] {
	return rows.filter(shouldShowOrgAdminRecordTypesTableRow);
}

function sortRecordTypes(
	rows: RecordType[],
	input?: RecordTypesListParams,
): RecordType[] {
	return sortConfigurationTypes(
		rows,
		input,
		RECORD_TYPE_ORDER_BY_ACCESSOR,
		"Name",
	);
}

export class RecordApiError extends Error {
	constructor(
		message: string,
		public readonly cause?: unknown,
		public readonly statusCode?: number,
	) {
		super(message);
		this.name = "RecordApiError";
	}

	static fromServiceResult(result: ServiceResult): RecordApiError {
		return new RecordApiError(
			result.message ?? "Record operation failed",
			undefined,
		);
	}
}

/** Form copy on a real record type can outlast the shared API timeout. */
const DUPLICATE_RECORD_TYPE_TIMEOUT_MS = 60_000;

export class RecordService {
	/**
	 * Create a new record type
	 * POST /records/types
	 *
	 * `organizationId` — sent as `organization-id` header. Pass `EMPTY_ORG_ID`
	 * for platform-level (system) record types, or the actual org ID for
	 * organization-level (custom) record types.
	 */
	async createRecordType(
		data: CreateRecordTypeInfo,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const validated = CreateRecordTypeInfoSchema.parse(data);
			const organizationId = options?.organizationId;
			const response = await apiService.post<ServiceResult>(
				"/records/types",
				validated,
				{
					headers:
						organizationId !== undefined
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw RecordApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to create record type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Duplicate a record type. The API copies core config and appends " copy" to the name.
	 * POST /records/types/{recordTypeId}/duplicate
	 */
	async duplicateRecordType(
		recordTypeId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.post<ServiceResult>(
				`/records/types/${recordTypeId}/duplicate`,
				undefined,
				{
					headers:
						organizationId !== undefined
							? { "organization-id": organizationId }
							: undefined,
					timeout: DUPLICATE_RECORD_TYPE_TIMEOUT_MS,
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw RecordApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to duplicate record type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get a single record type by ID
	 * GET /records/types/{recordTypeId}
	 *
	 * `organizationId` — sent as `Organization-id` header so platform admins
	 * acting in Org Admin resolve the record type in the correct scope.
	 */
	async getRecordTypeById(
		recordTypeId: string,
		options?: { subOrganizationId?: string; organizationId?: string },
	): Promise<RecordTypeDisplay> {
		try {
			const subOrganizationId = options?.subOrganizationId;
			const organizationId = options?.organizationId;
			const response = await apiService.get<unknown>(
				`/records/types/${recordTypeId}`,
				{
					params:
						subOrganizationId && subOrganizationId.length > 0
							? { subOrganizationId }
							: undefined,
					headers:
						organizationId && organizationId.length > 0
							? { "Organization-id": organizationId }
							: undefined,
				},
			);
			return RecordTypeDisplaySchema.parse(response);
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to get record type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update a record type
	 * PUT /records/types/{recordTypeId}
	 */
	async updateRecordType(
		recordTypeId: string,
		data: EditRecordTypeInfo,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const validated = EditRecordTypeInfoSchema.parse(data);
			const organizationId = options?.organizationId;
			const response = await apiService.put<ServiceResult>(
				`/records/types/${recordTypeId}`,
				validated,
				{
					headers:
						organizationId && organizationId.length > 0
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw RecordApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to update record type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Enable a record type for an organization
	 * PUT /records/types/{recordTypeId}/enable
	 */
	async enableRecordType(
		recordTypeId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.put<ServiceResult>(
				`/records/types/${recordTypeId}/enable`,
				undefined,
				{
					headers:
						organizationId && organizationId.length > 0
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw RecordApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to enable record type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Disable a record type for an organization
	 * PUT /records/types/{recordTypeId}/disable
	 */
	async disableRecordType(
		recordTypeId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.put<ServiceResult>(
				`/records/types/${recordTypeId}/disable`,
				undefined,
				{
					headers:
						organizationId && organizationId.length > 0
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw RecordApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to disable record type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Enable a specific sub-organization's access to a record type
	 * POST /records/types/{recordTypeId}/sub-organization/{subOrgId}/enable
	 */
	async enableRecordTypeSubOrganization(
		recordTypeId: string,
		subOrgId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		const organizationId = options?.organizationId;
		await apiService.post(
			`/records/types/${recordTypeId}/sub-organization/${subOrgId}/enable`,
			undefined,
			{
				headers:
					organizationId && organizationId.length > 0
						? { "organization-id": organizationId }
						: undefined,
			},
		);
	}

	/**
	 * Disable a specific sub-organization's access to a record type
	 * POST /records/types/{recordTypeId}/sub-organization/{subOrgId}/disable
	 */
	async disableRecordTypeSubOrganization(
		recordTypeId: string,
		subOrgId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		const organizationId = options?.organizationId;
		await apiService.post(
			`/records/types/${recordTypeId}/sub-organization/${subOrgId}/disable`,
			undefined,
			{
				headers:
					organizationId && organizationId.length > 0
						? { "organization-id": organizationId }
						: undefined,
			},
		);
	}

	/**
	 * Get all record types (simple list)
	 * GET /records/types
	 */
	async getRecordTypes(input?: RecordTypesAllParams): Promise<RecordType[]> {
		try {
			if (input?.OrganizationId || input?.subOrganizationId) {
				const scopedParams: RecordTypesListParams = {
					...(input.OrganizationId
						? { OrganizationId: input.OrganizationId }
						: {}),
					...(input.subOrganizationId
						? { SubOrganizationId: input.subOrganizationId }
						: {}),
				};
				const total = await this.getRecordTypesCount(scopedParams);
				if (total <= 0) {
					return [];
				}
				const fetchCount = Math.min(
					total,
					ORG_SCOPED_CONFIGURATION_TYPES_FETCH_CAP,
				);
				return this.getRecordTypesPage(1, fetchCount, scopedParams);
			}

			const params: Record<string, string> = {};
			if (input?.subOrganizationId) {
				params.subOrganizationId = input.subOrganizationId;
			}
			if (input?.OrganizationId) {
				params.OrganizationId = input.OrganizationId;
			}
			const response = await apiService.get<unknown>("/records/types", {
				params: Object.keys(params).length > 0 ? params : undefined,
			});
			return RecordTypesArraySchema.parse(response);
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to get record types: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get record types count
	 * GET /records/types/count
	 */
	async getRecordTypesCount(input?: RecordTypesListParams): Promise<number> {
		try {
			if (
				isOrgScopedConfigurationTypesQuery(input) &&
				input?.Enabled === undefined
			) {
				const merged = await this.getOrgScopedRecordTypesMerged(input);
				return merged.length;
			}

			return this.fetchRecordTypesCountSingle(input);
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to get record types count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	private async fetchRecordTypesCountSingle(
		input?: RecordTypesListParams,
	): Promise<number> {
		const response = await apiService.get<number>("/records/types/count", {
			params: input,
		});
		return z.number().parse(response);
	}

	/**
	 * Get a paginated page of record types
	 * GET /records/types/{start}/{count}
	 */
	async getRecordTypesPage(
		start: number,
		count: number,
		input?: RecordTypesListParams,
	): Promise<RecordType[]> {
		try {
			if (
				isOrgScopedConfigurationTypesQuery(input) &&
				input?.Enabled === undefined
			) {
				const merged = await this.getOrgScopedRecordTypesMerged(input);
				const sorted = sortRecordTypes(merged, input);
				return sliceConfigurationTypesPage(sorted, start, count);
			}

			return this.fetchRecordTypesPageSingle(start, count, input);
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to get record types page: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/** Enabled + disabled org-scoped rows merged for configuration tables. */
	private async getOrgScopedRecordTypesMerged(
		input?: RecordTypesListParams,
	): Promise<RecordType[]> {
		const baseParams = stripEnabledParam(input) ?? {};
		const enabledParams = { ...baseParams, Enabled: true };
		const disabledParams = { ...baseParams, Enabled: false };
		const [enabledCount, disabledCount] = await Promise.all([
			this.fetchRecordTypesCountSingle(enabledParams),
			this.fetchRecordTypesCountSingle(disabledParams),
		]);

		const [enabledRows, disabledRows] = await Promise.all([
			enabledCount > 0
				? this.fetchRecordTypesPageSingle(
						1,
						Math.min(enabledCount, ORG_SCOPED_CONFIGURATION_TYPES_FETCH_CAP),
						enabledParams,
					)
				: Promise.resolve([]),
			disabledCount > 0
				? this.fetchRecordTypesPageSingle(
						1,
						Math.min(disabledCount, ORG_SCOPED_CONFIGURATION_TYPES_FETCH_CAP),
						disabledParams,
					)
				: Promise.resolve([]),
		]);

		return filterOrgAdminRecordTypesTableRows(
			mergeConfigurationTypesById(enabledRows, disabledRows),
		);
	}

	private async fetchRecordTypesPageSingle(
		start: number,
		count: number,
		input?: RecordTypesListParams,
	): Promise<RecordType[]> {
		const response = await apiService.get<unknown>(
			`/records/types/${start}/${count}`,
			{ params: input },
		);
		return RecordTypesArraySchema.parse(response);
	}

	/**
	 * Get count of record instances, optionally filtered by record type(s) and org.
	 * GET /records/count
	 */
	async getRecordsCount(
		input?: RecordCountParams,
		options?: { signal?: AbortSignal },
	): Promise<number> {
		try {
			const response = await apiService.get<number>("/records/count", {
				params: toRecordQueryParams(input),
				headers: organizationIdHeader(input?.OrganizationId),
				signal: options?.signal,
			});
			return z.number().parse(response);
		} catch (error) {
			if (isRequestAborted(error)) throw error;
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to get records count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * DELETE /records/{recordId}
	 */
	async deleteRecord(
		recordId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const response = await apiService.delete<unknown>(
				`/records/${recordId}`,
				{
					headers: organizationIdHeader(options?.organizationId),
				},
			);
			if (response == null || response === "") {
				return { success: true, message: null, affectedId: null };
			}
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw RecordApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to delete record: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Delete a record type
	 * DELETE /records/types/{recordTypeId}
	 */
	async deleteRecordType(
		recordTypeId: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.delete<unknown>(
				`/records/types/${recordTypeId}`,
				{
					headers:
						organizationId && organizationId.length > 0
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			if (response == null || response === "") {
				return { success: true, message: null, affectedId: null };
			}
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw RecordApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to delete record type: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Export record types to CSV
	 * GET /records/types/{start}/{count}/export
	 */
	async exportRecordTypesCsv(
		start: number,
		count: number,
		input?: RecordTypesListParams,
		columns?: string | null,
	): Promise<Blob> {
		try {
			const params: RecordTypesListParams & { columns?: string } = {
				...input,
				...(columns != null && columns !== "" ? { columns } : {}),
			};
			return await apiService.get<Blob>(
				`/records/types/${start}/${count}/export`,
				{
					params,
					headers: { Accept: "text/csv" },
					responseType: "blob",
				},
			);
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to export record types: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== RECORD INSTANCES ====================

	/**
	 * GET /records/{start}/{count}
	 */
	async listRecords(
		start: number,
		count: number,
		params?: RecordInstanceListParams,
	): Promise<RecordDisplay[]> {
		try {
			const response = await apiService.get<unknown>(
				`/records/${start}/${count}`,
				{
					params: toRecordQueryParams(params),
					headers: organizationIdHeader(params?.OrganizationId),
				},
			);
			return RecordsArray.parse(response);
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to list records: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Export record instances to CSV.
	 * GET /records/{start}/{count}/export
	 *
	 * @param start - Starting index (1-based)
	 * @param count - Number of records to export
	 * @param params - Optional query parameters (same as list/count) plus columns
	 * @returns Blob containing CSV data
	 */
	async exportRecords(
		start: number,
		count: number,
		params?: RecordInstanceListParams & { columns?: string },
	): Promise<Blob> {
		try {
			return await apiService.get<Blob>(`/records/${start}/${count}/export`, {
				params,
				responseType: "blob",
				headers: {
					Accept: "text/csv",
					...organizationIdHeader(params?.OrganizationId),
				} as Record<string, string>,
			});
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to export records: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get geometry source options for records.
	 * GET /records/geometry-sources
	 */
	async getGeometrySources(): Promise<GeometrySourceDisplay[]> {
		try {
			const response = await apiService.get<unknown>(
				"/records/geometry-sources",
			);
			return GeometrySourcesArraySchema.parse(response);
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to get record geometry sources: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get GPS method options for records.
	 * GET /records/gps-methods
	 */
	async getGpsMethods(): Promise<GpsMethodDisplay[]> {
		try {
			const response = await apiService.get<unknown>("/records/gps-methods");
			return GpsMethodsArraySchema.parse(response);
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to get record GPS methods: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /records/{id}
	 * Org context via `organization-id` header (same pattern as jobs/tickets).
	 */
	async getRecord(
		recordId: string,
		options?: { organizationId?: string },
	): Promise<RecordDisplayWithUsers> {
		try {
			const response = await apiService.get<unknown>(`/records/${recordId}`, {
				headers: organizationIdHeader(options?.organizationId),
			});
			return RecordDisplayWithUsersSchema.parse(response);
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to get record: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * PUT /records/{recordId}
	 */
	async updateRecord(
		recordId: string,
		body: EditRecordInfo,
	): Promise<ServiceResult> {
		try {
			const payload = EditRecordInfoSchema.parse(body);
			const response = await apiService.put<ServiceResult>(
				`/records/${recordId}`,
				payload,
				{
					headers: organizationIdHeader(payload.organizationId ?? undefined),
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw RecordApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to update record: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Create a record instance.
	 * POST /records
	 */
	async createRecord(data: CreateRecordInfo): Promise<ServiceResult> {
		try {
			const payload = CreateRecordInfoSchema.parse(data);
			const response = await apiService.post<ServiceResult>(
				"/records",
				payload,
				{
					headers: organizationIdHeader(payload.organizationId ?? undefined),
				},
			);
			const result = ServiceResultSchema.parse(response);
			if (!result.success) {
				throw RecordApiError.fromServiceResult(result);
			}
			return result;
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to create record: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /records/{id}/history/{start}/{count}
	 */
	async getRecordHistory(
		recordId: string,
		start: number,
		count: number,
	): Promise<AuditLogDisplay[]> {
		try {
			const response = await apiService.get<AuditLogDisplay[]>(
				`/records/${recordId}/history/${start}/${count}`,
			);
			return AuditLogArray.parse(response);
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to get record history: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /records/{id}/history/count
	 */
	async getRecordHistoryCount(recordId: string): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/records/${recordId}/history/count`,
			);
			return z.number().parse(response);
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to get record history count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== RECORD TYPE HISTORY ====================

	/**
	 * Get record type history count
	 * GET /records/types/{id}/history/count
	 */
	async getRecordTypeHistoryCount(
		recordTypeId: string,
		organizationId?: string,
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/records/types/${recordTypeId}/history/count`,
				{
					headers: organizationId
						? { "organization-id": organizationId }
						: undefined,
				},
			);
			return z.number().parse(response);
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to get record type history count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Get record type history with pagination
	 * GET /records/types/{id}/history/{start}/{count}
	 */
	async getRecordTypeHistory(
		recordTypeId: string,
		start: number = 1,
		count: number = 10,
		organizationId?: string,
	): Promise<AuditLogDisplay[]> {
		try {
			const response = await apiService.get<AuditLogDisplay[]>(
				`/records/types/${recordTypeId}/history/${start}/${count}`,
				{
					headers: organizationId
						? { "organization-id": organizationId }
						: undefined,
				},
			);
			return AuditLogArray.parse(response);
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to get record type history: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== RECORD FILES ====================

	private toRecordFileQueryParams(
		params?: RecordFileParams,
	):
		| Record<string, string | number | boolean | string[] | undefined>
		| undefined {
		if (!params) return undefined;
		const flat: Record<
			string,
			string | number | boolean | string[] | undefined
		> = {};
		const { OrderBy, ...rest } = params;
		for (const [k, v] of Object.entries(rest)) {
			if (v !== undefined) flat[k] = v as string | number | boolean | string[];
		}
		if (OrderBy?.Fields !== undefined) flat["OrderBy.Fields"] = OrderBy.Fields;
		if (OrderBy?.Descending !== undefined)
			flat["OrderBy.Descending"] = OrderBy.Descending;
		return Object.keys(flat).length ? flat : undefined;
	}

	/**
	 * GET /records/{recordId}/files/{start}/{count}
	 */
	async getRecordFiles(
		recordId: string,
		start: number = 1,
		count: number = 50,
		params?: RecordFileParams,
		options?: { organizationId?: string },
	): Promise<RecordFileDisplay[]> {
		try {
			const response = await apiService.get<unknown>(
				`/records/${recordId}/files/${start}/${count}`,
				{
					params: this.toRecordFileQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);
			return RecordFileDisplayArraySchema.parse(response);
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to get files for record ${recordId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /records/{recordId}/files/count
	 */
	async getRecordFilesCount(
		recordId: string,
		params?: RecordFileParams,
		options?: { organizationId?: string },
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/records/${recordId}/files/count`,
				{
					params: this.toRecordFileQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);
			return z.number().parse(response);
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to get file count for record ${recordId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * POST /records/{recordId}/files — multipart field `File`.
	 */
	async uploadRecordFile(
		recordId: string,
		file: File | Blob,
		options?: {
			organizationId?: string;
			description?: string;
			entityAttachmentRequirementTagId?: string | null;
			onUploadProgress?: (percent: number) => void;
		},
	): Promise<void> {
		try {
			const formData = new FormData();
			const filename =
				file instanceof File && file.name.length > 0 ? file.name : "upload.bin";
			formData.append("File", file, filename);
			appendAttachmentRequirementTagId(
				formData,
				options?.entityAttachmentRequirementTagId,
			);

			await apiService.post(`/records/${recordId}/files`, formData, {
				params: options?.description
					? { Description: options.description }
					: undefined,
				headers: {
					"Content-Type": "multipart/form-data",
					...organizationIdHeader(options?.organizationId),
				},
				onUploadProgress: (event) => {
					if (!options?.onUploadProgress || !event.total) return;
					options.onUploadProgress(
						Math.round((event.loaded / event.total) * 100),
					);
				},
			});
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to upload file for record ${recordId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * DELETE /records/{recordId}/files/{fileId}
	 */
	async deleteRecordFile(
		recordId: string,
		fileId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await apiService.delete(`/records/${recordId}/files/${fileId}`, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to delete file ${fileId} for record ${recordId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * PUT /records/{recordId}/files/{recordFileId}
	 */
	async editRecordFile(
		recordId: string,
		recordFileId: string,
		info: EditRecordFileInfo,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await apiService.put(`/records/${recordId}/files/${recordFileId}`, info, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to edit file ${recordFileId} for record ${recordId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /records/{recordId}/files/{fileId}
	 */
	async getRecordFile(
		recordId: string,
		fileId: string,
		options?: { organizationId?: string },
	): Promise<Blob> {
		try {
			return await apiService.get<Blob>(
				`/records/${recordId}/files/${fileId}`,
				{
					responseType: "blob",
					headers: organizationIdHeader(options?.organizationId),
				},
			);
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to get file ${fileId} for record ${recordId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /records/{recordId}/files/{fileId}/thumbnail
	 */
	async getRecordFileThumbnail(
		recordId: string,
		fileId: string,
		options?: { organizationId?: string },
	): Promise<Blob> {
		try {
			return await apiService.get<Blob>(
				`/records/${recordId}/files/${fileId}/thumbnail`,
				{
					responseType: "blob",
					headers: organizationIdHeader(options?.organizationId),
				},
			);
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to get thumbnail for file ${fileId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * HEAD /records/{recordId}/files/{fileId}
	 */
	async headRecordFile(
		recordId: string,
		fileId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await apiService.head(`/records/${recordId}/files/${fileId}`, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to head file ${fileId} for record ${recordId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * PATCH /records/{recordId}/files/{fileId} — multipart field `file`.
	 */
	async replaceRecordFile(
		recordId: string,
		fileId: string,
		file: File | Blob,
		options?: {
			organizationId?: string;
			onUploadProgress?: (percent: number) => void;
		},
	): Promise<void> {
		try {
			const formData = new FormData();
			const filename =
				file instanceof File && file.name.length > 0 ? file.name : "upload.bin";
			formData.append("file", file, filename);

			await apiService.patch(`/records/${recordId}/files/${fileId}`, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
					...organizationIdHeader(options?.organizationId),
				},
				onUploadProgress: (event) => {
					if (!options?.onUploadProgress || !event.total) return;
					options.onUploadProgress(
						Math.round((event.loaded / event.total) * 100),
					);
				},
			});
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to replace file ${fileId} for record ${recordId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== RECORD NOTES ====================

	/**
	 * Flattens {@link RecordNoteParams} for ASP.NET query-string binding.
	 */
	private toRecordNoteQueryParams(
		params?: RecordNoteParams,
	):
		| Record<string, string | number | boolean | string[] | undefined>
		| undefined {
		return this.toRecordFileQueryParams(params);
	}

	/**
	 * List record notes with pagination.
	 * GET /records/{recordId}/notes/{start}/{count}
	 */
	async getRecordNotes(
		recordId: string,
		start: number = 1,
		count: number = 50,
		params?: RecordNoteParams,
		options?: { organizationId?: string },
	): Promise<NoteDisplay[]> {
		try {
			const response = await apiService.get<unknown>(
				`/records/${recordId}/notes/${start}/${count}`,
				{
					params: this.toRecordNoteQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);
			return RecordNoteDisplayArraySchema.parse(response);
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to get notes for record ${recordId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Count record notes.
	 * GET /records/{recordId}/notes/count
	 */
	async getRecordNotesCount(
		recordId: string,
		params?: RecordNoteParams,
		options?: { organizationId?: string },
	): Promise<number> {
		try {
			const response = await apiService.get<number>(
				`/records/${recordId}/notes/count`,
				{
					params: this.toRecordNoteQueryParams(params),
					headers: organizationIdHeader(
						options?.organizationId ?? params?.OrganizationId,
					),
				},
			);
			return z.number().parse(response);
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to get note count for record ${recordId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Create a record note.
	 * POST /records/{recordId}/notes
	 */
	async createRecordNote(
		recordId: string,
		info: CreateNoteInfo,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			const body = CreateNoteInfoSchema.parse(info);
			await apiService.post(`/records/${recordId}/notes`, body, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to create note for record ${recordId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Update a record note.
	 * PUT /records/{recordId}/notes/{noteId}
	 */
	async updateRecordNote(
		recordId: string,
		noteId: string,
		info: CreateNoteInfo,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			const body = CreateNoteInfoSchema.parse(info);
			await apiService.put(`/records/${recordId}/notes/${noteId}`, body, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to update note ${noteId} for record ${recordId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Delete a record note.
	 * DELETE /records/{recordId}/notes/{noteId}
	 */
	async deleteRecordNote(
		recordId: string,
		noteId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await apiService.delete(`/records/${recordId}/notes/${noteId}`, {
				headers: organizationIdHeader(options?.organizationId),
			});
		} catch (error) {
			if (error instanceof RecordApiError) {
				throw error;
			}
			throw new RecordApiError(
				`Failed to delete note ${noteId} for record ${recordId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	async getRecordTypeAttachmentRequirement(
		recordTypeId: string,
		options?: { organizationId?: string },
	) {
		try {
			return await getTypeAttachmentRequirement(
				"records",
				recordTypeId,
				options?.organizationId,
			);
		} catch (error) {
			if (error instanceof RecordApiError) throw error;
			throw new RecordApiError(
				`Failed to get attachment requirement for record type ${recordTypeId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	async putRecordTypeAttachmentRequirement(
		recordTypeId: string,
		minimumTotalAttachments: number,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await putTypeAttachmentRequirement(
				"records",
				recordTypeId,
				minimumTotalAttachments,
				options?.organizationId,
			);
		} catch (error) {
			if (error instanceof RecordApiError) throw error;
			throw new RecordApiError(
				`Failed to save attachment requirement for record type ${recordTypeId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	async createRecordTypeAttachmentRequirementTag(
		recordTypeId: string,
		info: {
			name?: string | null;
			description?: string | null;
			allowedKind?: string | null;
			minimumCount: number;
			sortOrder?: number | null;
		},
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await createTypeAttachmentRequirementTag(
				"records",
				recordTypeId,
				info,
				options?.organizationId,
			);
		} catch (error) {
			if (error instanceof RecordApiError) throw error;
			throw new RecordApiError(
				`Failed to create attachment requirement tag for record type ${recordTypeId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	async editRecordTypeAttachmentRequirementTag(
		recordTypeId: string,
		tagId: string,
		info: {
			name?: string | null;
			description?: string | null;
			allowedKind?: string | null;
			minimumCount: number;
			sortOrder: number;
		},
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await editTypeAttachmentRequirementTag(
				"records",
				recordTypeId,
				tagId,
				info,
				options?.organizationId,
			);
		} catch (error) {
			if (error instanceof RecordApiError) throw error;
			throw new RecordApiError(
				`Failed to edit attachment requirement tag ${tagId} for record type ${recordTypeId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	async deleteRecordTypeAttachmentRequirementTag(
		recordTypeId: string,
		tagId: string,
		options?: { organizationId?: string },
	): Promise<void> {
		try {
			await deleteTypeAttachmentRequirementTag(
				"records",
				recordTypeId,
				tagId,
				options?.organizationId,
			);
		} catch (error) {
			if (error instanceof RecordApiError) throw error;
			throw new RecordApiError(
				`Failed to delete attachment requirement tag ${tagId} for record type ${recordTypeId}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}
}

export const recordService = new RecordService();
